import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit } from "@/hooks/use-rate-limit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Loader2 } from "lucide-react";
import { z } from "zod";
import loginBg from "@/assets/login-bg.jpg";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Checkbox } from "@/components/ui/checkbox";

const signupSchema = z.object({
  fullName: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name is too long"),
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email is too long"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password is too long")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  confirmPassword: z.string(),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: "You must agree to the Terms of Service and Privacy Policy"
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const getPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  return checks;
};

const Signup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState({ length: false, uppercase: false, lowercase: false, number: false });
  const [showExistingUserDialog, setShowExistingUserDialog] = useState(false);
  const [existingEmail, setExistingEmail] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { checkRateLimit } = useRateLimit();

  // Get plan and billing frequency from URL params if present
  const searchParams = new URLSearchParams(window.location.search);
  const selectedPlan = searchParams.get('plan');
  const selectedBilling = searchParams.get('billing') as 'monthly' | 'annual' | null;

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
  });

  useEffect(() => {
    // Fetch the logo from any user's profile (public branding)
    const fetchLogo = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('north_logo_url')
        .not('north_logo_url', 'is', null)
        .limit(1)
        .maybeSingle();
      
      if (data?.north_logo_url) {
        setLogoUrl(data.north_logo_url);
      }
    };
    fetchLogo();
  }, []);

  const handleSignup = async (values: z.infer<typeof signupSchema>) => {
    setIsLoading(true);
    setRateLimitMessage(null);

    try {
      // Check captcha token (only when Turnstile is configured)
      if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
        toast({
          title: "Verification required",
          description: "Please complete the security check",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Check rate limit before attempting signup (rate limit by IP simulation using email domain)
      const rateLimitResult = await checkRateLimit("signup", values.email.toLowerCase().trim());
      if (!rateLimitResult.allowed) {
        setRateLimitMessage(rateLimitResult.message || "Too many signup attempts. Please try again later.");
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          captchaToken,
          data: {
            full_name: values.fullName,
            // Pass selected plan and billing frequency to auth hook
            selected_plan: selectedPlan || null,
            selected_billing: selectedBilling || 'monthly',
          },
          emailRedirectTo: selectedPlan
            ? `${window.location.origin}/select-plan?plan=${selectedPlan}&billing=${selectedBilling || 'monthly'}&checkout=true`
            : `${window.location.origin}/select-plan`,
        },
      });

      if (error) {
        // Reset turnstile on error
        turnstileRef.current?.reset();
        setCaptchaToken(null);

        // Check if error is about user already existing
        if (error.message.toLowerCase().includes('already') ||
            error.message.toLowerCase().includes('exists') ||
            error.message.toLowerCase().includes('registered')) {
          setExistingEmail(values.email);
          setShowExistingUserDialog(true);
          setIsLoading(false);
          return;
        }
        throw error;
      }

      // Notify admin of new signup
      await supabase.functions.invoke('notify-admin-signup', {
        body: {
          fullName: values.fullName,
          email: values.email
        }
      });

      toast({
        title: "Account created successfully!",
        description: selectedPlan 
          ? "Redirecting to checkout..." 
          : "Redirecting to select your plan...",
      });

      // Show loading overlay during redirect
      setIsRedirecting(true);

      // Wait for auth hook to complete (creates team, customer, etc.)
      // Then redirect based on whether plan was selected
      setTimeout(() => {
        if (selectedPlan) {
          // Plan selected on landing page → go to checkout
          navigate(`/select-plan?plan=${selectedPlan}&billing=${selectedBilling || 'monthly'}&checkout=true`);
        } else {
          // No plan selected → go to select-plan page
          navigate('/select-plan');
        }
      }, 2000);
    } catch (error: any) {
      // Reset turnstile on error
      turnstileRef.current?.reset();
      setCaptchaToken(null);

      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginBg})` }}
      />
      {/* Black overlay */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Back to home link */}
      <Link
        to="/"
        className="absolute top-8 left-8 z-20 flex items-center gap-2 text-white hover:text-white/80 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to home</span>
      </Link>

      {/* Content */}
      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="space-y-6">
          <div className="flex justify-center pt-2">
            <Logo customLogoUrl={logoUrl} size="lg" />
          </div>
          <CardTitle className="text-2xl text-center">Create an account</CardTitle>
          <CardDescription className="text-center">
            Start your 14-day free trial
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSignup)} className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="••••••••" 
                        {...field} 
                        disabled={isLoading}
                        onChange={(e) => {
                          field.onChange(e);
                          setPasswordStrength(getPasswordStrength(e.target.value));
                        }}
                      />
                    </FormControl>
                    <div className="mt-2 space-y-1 text-xs">
                      <div className={passwordStrength.length ? "text-green-600" : "text-muted-foreground"}>
                        {passwordStrength.length ? "✓" : "○"} At least 8 characters
                      </div>
                      <div className={passwordStrength.uppercase ? "text-green-600" : "text-muted-foreground"}>
                        {passwordStrength.uppercase ? "✓" : "○"} One uppercase letter
                      </div>
                      <div className={passwordStrength.lowercase ? "text-green-600" : "text-muted-foreground"}>
                        {passwordStrength.lowercase ? "✓" : "○"} One lowercase letter
                      </div>
                      <div className={passwordStrength.number ? "text-green-600" : "text-muted-foreground"}>
                        {passwordStrength.number ? "✓" : "○"} One number
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="agreeToTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        I agree to the{" "}
                        <Link to="/terms-of-service" target="_blank" className="text-primary hover:underline">
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Turnstile CAPTCHA */}
              {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={setCaptchaToken}
                    onError={() => setCaptchaToken(null)}
                    onExpire={() => setCaptchaToken(null)}
                    options={{
                      theme: "light",
                      size: "normal",
                    }}
                  />
                </div>
              )}

              <Button type="submit" variant="gold" className="w-full" disabled={isLoading || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)}>
                {isLoading ? "Creating account..." : "Sign up"}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={async () => {
                  const { error } = await supabase.auth.signInWithOAuth({
                    provider: 'google',
                options: {
                  redirectTo: `${window.location.origin}/select-plan${selectedPlan ? `?plan=${selectedPlan}` : ''}`
                }
                  });
                  if (error) {
                    toast({
                      title: "Error",
                      description: error.message,
                      variant: "destructive",
                    });
                  }
                }}
                disabled={isLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Rate Limit Warning */}
      {rateLimitMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-lg p-4 max-w-md shadow-lg">
          <p className="text-sm font-medium text-red-800 mb-1">Too Many Attempts</p>
          <p className="text-sm text-red-800">{rateLimitMessage}</p>
        </div>
      )}

      {/* Existing User Dialog */}
      <AlertDialog open={showExistingUserDialog} onOpenChange={setShowExistingUserDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account Already Exists</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>An account with the email <strong>{existingEmail}</strong> already exists.</p>
              <p>Would you like to:</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExistingUserDialog(false);
                navigate('/login');
              }}
              className="w-full sm:w-auto"
            >
              Sign in instead
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowExistingUserDialog(false);
                navigate('/login');
                // User can use forgot password from login page
              }}
              className="w-full sm:w-auto"
            >
              Reset password
            </Button>
            <AlertDialogAction onClick={() => setShowExistingUserDialog(false)}>
              Try different email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Loading Overlay during redirect */}
      {isRedirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 text-white animate-spin" />
            <p className="text-white text-lg font-medium">
              {selectedPlan ? "Setting up your account..." : "Redirecting to select your plan..."}
            </p>
            <p className="text-white/70 text-sm">Please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
